import { useState } from 'react';
import { match } from 'ts-pattern';

import TooltipButton from '@/components/Button/TooltipButton';
import { ConfirmActionDialog } from '@/components/Dialog/ConfirmActionDialog';
import type { IconName } from '@/components/Icon/iconMap';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { defaultNS, useTranslation } from '@/lib/i18n';

import useAppUserCreate from '../../hooks/useAppUserCreate';
import useAppUserDeactivate from '../../hooks/useAppUserDeactivate';
import useAppUserReactivate from '../../hooks/useAppUserReactivate';
import type { AppUser, AppUserGroupRef, GroupViolation } from '../../types';
import type { AppUserAction } from '../../types/action';
import { suggestedAction } from '../../types/action';
import { GroupProvisioningDialog } from '../GroupProvisioningDialog';

export type AppUserActionProps = {
  appIntegrationId: string;
  appUserId?: string;
  personId?: string;
  appName: string;
  action?: AppUserAction;
  onSuccess?: () => void;
  softwareId?: string;
  groupViolation?: GroupViolation;
  user?: AppUser;
};

/** AppUserのRuleMatchに合わせてDressCodeが操作者に推奨するアクションを実行するためのボタンコンポーネント */
export const AppUserActionButton = ({
  appIntegrationId,
  appUserId,
  personId,
  appName,
  action,
  onSuccess,
  softwareId,
  groupViolation,
  user,
}: AppUserActionProps) => {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'domain.appUser',
  });
  const { t: tPage } = useTranslation(defaultNS, {
    keyPrefix: 'pages.profile.app.users',
  });
  const tc = useCommonTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isGroupDialogOpen, setIsGroupDialogOpen] = useState(false);

  const createAppUser = useAppUserCreate({
    appIntegrationId,
    personId: personId || '',
    onSuccess,
  });

  const reactivateAppUser = useAppUserReactivate({
    appIntegrationId,
    appUserId: appUserId || '',
    onSuccess,
  });

  const deactivateAppUser = useAppUserDeactivate({
    appIntegrationId,
    appUserId: appUserId || '',
    onSuccess,
  });

  const isLoading =
    createAppUser.isLoading ||
    reactivateAppUser.isLoading ||
    deactivateAppUser.isLoading;

  const getButtonProps = () =>
    match(action?.type)
      .with(suggestedAction.CREATE, () => ({
        icon: 'UserRoundPlusIcon' as const satisfies IconName,
        text: t('component.AppUserActionButton.createUser'),
        tooltipKey: 'component.AppUserActionButton.toolTipCreateUserOnWebPage',
        dialogTitle: {
          resource: t('appUser'),
          action: tc('action.create'),
        },
        onClick: () => {
          createAppUser.mutate();
        },
      }))
      .with(suggestedAction.REACTIVATE, () => ({
        icon: 'UserRoundPlusIcon' as const satisfies IconName,
        text: t('component.AppUserActionButton.reactivateUser'),
        tooltipKey:
          'component.AppUserActionButton.toolTipReactivateUserOnWebPage',
        dialogTitle: {
          resource: t('appUser'),
          action: tc('action.reactivate'),
        },
        onClick: () => {
          reactivateAppUser.mutate();
        },
      }))
      .with(suggestedAction.DEACTIVATE, () => ({
        icon: 'UserRoundMinusIcon' as const satisfies IconName,
        text: t('component.AppUserActionButton.deactivateUser'),
        tooltipKey:
          'component.AppUserActionButton.toolTipDeactivateUserOnWebPage',
        dialogTitle: {
          resource: t('appUser'),
          action: tc('action.deactivate'),
        },
        onClick: () => setIsDialogOpen(true),
      }))
      .with(suggestedAction.GROUP_JOIN, () => ({
        icon: 'UserRoundPlusIcon' as const satisfies IconName,
        text: tPage('action.groupJoin'),
        tooltipKey: '',
        dialogTitle: { resource: '', action: '' },
        onClick: () => setIsGroupDialogOpen(true),
      }))
      .with(suggestedAction.GROUP_LEAVE, () => ({
        icon: 'UserRoundMinusIcon' as const satisfies IconName,
        text: tPage('action.groupLeave'),
        tooltipKey: '',
        dialogTitle: { resource: '', action: '' },
        onClick: () => setIsGroupDialogOpen(true),
      }))
      .otherwise(() => null);

  const buttonProps = getButtonProps();
  if (!buttonProps) return null;

  const handleAction = () => {
    deactivateAppUser.mutate();
    setIsDialogOpen(false);
  };

  const getDescription = (action: AppUserAction): string => {
    if ('describe' in action) {
      return action.describe;
    }
    return t('component.AppUserActionButton.describe');
  };

  if (!action) {
    return <div>{t('component.AppUserActionButton.noRecommendedAction')}</div>;
  }

  const isGroupAction =
    action.type === suggestedAction.GROUP_JOIN ||
    action.type === suggestedAction.GROUP_LEAVE;

  const isGroupActionDisabled = isGroupAction && (!softwareId || !user);

  const groupDialogGroups: AppUserGroupRef[] = (() => {
    if (!groupViolation) return [];
    if (action.type === suggestedAction.GROUP_JOIN)
      return groupViolation.noGroup;
    if (action.type === suggestedAction.GROUP_LEAVE)
      return groupViolation.violatedGroup;
    return [];
  })();

  return (
    <>
      <TooltipButton
        icon={buttonProps.icon}
        variant="outline"
        iconColor="primary"
        isLoading={isLoading}
        onClick={(e) => {
          e.stopPropagation();
          buttonProps.onClick();
        }}
        disabled={action.disabled || isLoading || isGroupActionDisabled}
        content={
          action.disabled && buttonProps.tooltipKey
            ? t(buttonProps.tooltipKey, {
                defaultValue: '',
                name: appName,
              })
            : undefined
        }
      >
        {buttonProps.text}
      </TooltipButton>
      <ConfirmActionDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onConfirm={handleAction}
        title={buttonProps.dialogTitle}
        description={getDescription(action)}
      />
      {isGroupAction && softwareId && user && (
        <GroupProvisioningDialog
          open={isGroupDialogOpen}
          onOpenChange={setIsGroupDialogOpen}
          softwareId={softwareId}
          appIntegrationId={appIntegrationId}
          appUser={user}
          groups={groupDialogGroups}
          mode={action.type === suggestedAction.GROUP_JOIN ? 'join' : 'leave'}
        />
      )}
    </>
  );
};
